/**
 * Medir un IFC del proyecto en el navegador SIN montar el visor 3D («Cambió el plano»). Cada medición usa un
 * Worker propio que se termina al acabar: medir A y luego B (en secuencia) nunca tiene dos modelos abiertos.
 *
 * Los parámetros de medición son los de la calibración vigente (los mismos del visor) y los materiales sin tipo se
 * reconocen como en el visor (`clasificarMaterialesIfc`, con caché por nombre): el inventario sale igual al que
 * vio quien armó la hoja.
 */
import type { ParametrosMedicionIfc } from '@supernova/shared';
import { clasificarMaterialesIfc } from '../api/planos.js';
import type { FaseDeMedir, MensajeDeMedir, PedidoMedir, TiposDeMaterialAlMedidor } from './ifcMedir.worker.js';
import type { MedicionDeModelo } from './medirModeloIfc.js';
import { WEBIFC_URL, WEBIFC_WASM } from './webIfc.js';

/** La URL con la que el visor descarga el IFC que se subió al proyecto. */
export function urlDelIfcDelProyecto(projectId: string, fileId: string): string {
  return `/api/v1/projects/${projectId}/files/${fileId}/download`;
}

export interface AvanceDeMedir {
  fase: FaseDeMedir;
  fraccion: number | null;
}

export interface PedidoMedirSinVisor {
  projectId: string;
  fileId: string;
  parametros: ParametrosMedicionIfc;
  onAvance?: (a: AvanceDeMedir) => void;
  signal?: AbortSignal;
}

/** Mide un IFC del proyecto. Rechaza con un Error de texto para la persona si no se puede leer. */
export function medirIfcSinVisor(p: PedidoMedirSinVisor): Promise<MedicionDeModelo> {
  return new Promise((resolve, reject) => {
    if (p.signal?.aborted) {
      reject(new DOMException('Cancelado', 'AbortError'));
      return;
    }
    const worker = new Worker(new URL('./ifcMedir.worker.ts', import.meta.url), { type: 'module' });
    let terminado = false;
    const terminar = () => {
      if (terminado) return;
      terminado = true;
      worker.terminate();
      p.signal?.removeEventListener('abort', alCancelar);
    };
    const alCancelar = () => {
      terminar();
      reject(new DOMException('Cancelado', 'AbortError'));
    };
    p.signal?.addEventListener('abort', alCancelar);
    worker.onmessage = (ev: MessageEvent<MensajeDeMedir>) => {
      if (terminado) return;
      const m = ev.data;
      if (m.tipo === 'avance') {
        p.onAvance?.({ fase: m.fase, fraccion: m.fraccion });
        return;
      }
      if (m.tipo === 'materiales') {
        const contestar = (t: TiposDeMaterialAlMedidor) => {
          if (!terminado) worker.postMessage(t);
        };
        void clasificarMaterialesIfc(p.projectId, p.fileId, m.nombres).then((r) =>
          contestar({ tipo: 'tiposDeMaterial', materiales: r.materiales, aviso: r.avisoIa }),
        );
        return;
      }
      if (m.tipo === 'error') {
        terminar();
        reject(new Error(m.msg || 'No se pudo leer el modelo.'));
        return;
      }
      terminar();
      const { tipo: _tipo, ...medicion } = m;
      resolve(medicion);
    };
    worker.onerror = (e) => {
      terminar();
      reject(new Error(e.message || 'No se pudo leer el modelo.'));
    };
    const pedido: PedidoMedir = {
      tipo: 'medir',
      url: new URL(urlDelIfcDelProyecto(p.projectId, p.fileId), window.location.href).href,
      webIfcUrl: WEBIFC_URL,
      wasmPath: WEBIFC_WASM,
      parametros: p.parametros,
    };
    worker.postMessage(pedido);
  });
}

/** Texto del avance para la persona (sin jerga). */
export function textoDeAvanceDeMedir(a: AvanceDeMedir | null): string {
  if (!a) return 'Preparando…';
  switch (a.fase) {
    case 'motor':
      return 'Preparando la lectura del modelo…';
    case 'descarga':
      return 'Descargando el modelo…';
    case 'apertura':
      return 'Abriendo el modelo…';
    case 'inventario':
      return 'Contando las piezas…';
    case 'geometria':
      return 'Midiendo cada pieza…';
    case 'cierre':
      return 'Terminando de medir…';
  }
}
